import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const highlights = [
  {
    label: '日給',
    value: '11,000円〜',
    note: '経験・スキルにより優遇',
    icon: 'ri-money-yen-circle-line',
  },
  {
    label: '入社祝い金',
    value: '40,000円',
    note: '条件達成後に支給',
    icon: 'ri-gift-line',
  },
];

const conditions = [
  { icon: 'ri-calendar-check-line', label: '勤務日数', value: '週2日〜OK（フルタイムも歓迎）' },
  { icon: 'ri-sun-line', label: '対応シフト', value: '日勤' },
  { icon: 'ri-bank-card-line', label: '日払い制度', value: 'あり' },
  { icon: 'ri-car-line', label: '交通費', value: '全額支給（当社規定）' },
  { icon: 'ri-shield-check-line', label: '各種保険', value: '社会保険・労災保険 適用' },
  { icon: 'ri-graduation-cap-line', label: '法定研修', value: '入社時に実施（未経験者も安心）' },
  { icon: 'ri-t-shirt-line', label: '制服・装備品', value: '貸与（預り金あり）' },
];

export default function Recruitment() {
  const { ref, isVisible } = useScrollAnimation();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const form = e.currentTarget;
    const params = new URLSearchParams();
    new FormData(form).forEach((v, k) => params.append(k, v.toString()));
    try {
      const res = await fetch('https://readdy.ai/api/form/d71mgark1jkj467r5j5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) { setSubmitted(true); form.reset(); }
      else setError('送信に失敗しました。もう一度お試しください。');
    } catch {
      setError('通信エラーが発生しました。もう一度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="recruitment" className="py-24 bg-[#f4f7fb]">
      <div className="max-w-4xl mx-auto px-6">
        <div ref={ref} className={`fade-in-up ${isVisible ? 'visible' : ''}`}>

          {/* ヘッダー */}
          <div className="text-center mb-12">
            <span className="text-[#38BDF8] text-sm font-medium tracking-widest">RECRUITMENT</span>
            <h2 className="text-[#0D1A4E] text-3xl md:text-4xl font-black mt-3 mb-4">採用情報</h2>
            <div className="w-12 h-1 bg-[#38BDF8] mx-auto"></div>
            <p className="text-gray-500 mt-5 text-sm leading-relaxed">
              設立2年、会社はまだまだ伸び盛りです。一緒に大きくなっていける仲間を探しています。<br />
              未経験OK・週2日〜。まずはお気軽にご応募ください。
            </p>
          </div>

          {/* 給与ハイライト */}
          <div className="grid sm:grid-cols-2 gap-5 mb-6">
            {highlights.map((h) => (
              <div
                key={h.label}
                className="bg-[#0D1A4E] rounded-lg p-7 flex items-center gap-5"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#38BDF8]/15 rounded-lg flex-shrink-0">
                  <i className={`${h.icon} text-[#38BDF8] text-3xl`}></i>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-medium mb-1">{h.label}</p>
                  <p className="text-[#38BDF8] text-2xl font-black leading-none mb-1">{h.value}</p>
                  <p className="text-white/50 text-xs">{h.note}</p>
                </div>
              </div>
            ))}
          </div>

          {/* 勤務条件一覧 */}
          <div className="bg-white border border-gray-100 rounded-lg overflow-hidden mb-10">
            <div className="px-7 py-4 border-b border-gray-100 bg-gray-50/60">
              <p className="text-[#0D1A4E] text-sm font-black">勤務条件</p>
            </div>
            <div className="divide-y divide-gray-100">
              {conditions.map((c, idx) => (
                <div key={idx} className="flex items-center gap-4 px-7 py-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#38BDF8]/10 rounded-md flex-shrink-0">
                    <i className={`${c.icon} text-[#38BDF8] text-base`}></i>
                  </div>
                  <span className="text-[#0D1A4E] text-sm font-bold w-28 flex-shrink-0">{c.label}</span>
                  <span className="text-gray-600 text-sm">{c.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 応募フォーム */}
          <div className="bg-white border border-gray-100 rounded-lg overflow-hidden">
            <div className="px-8 py-5 border-b border-gray-100 bg-gray-50/60">
              <p className="text-[#0D1A4E] font-black text-base">応募フォーム</p>
              <p className="text-gray-500 text-xs mt-1">内容確認後、担当者より2営業日以内にご連絡いたします。</p>
            </div>

            {submitted ? (
              <div className="p-10 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-[#0D1A4E] font-bold text-xl mb-2">応募を受け付けました</h3>
                <p className="text-gray-500 text-sm">担当者より2営業日以内にご連絡いたします。</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-sm text-[#38BDF8] hover:underline cursor-pointer whitespace-nowrap"
                >
                  続けて応募する
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="recruitment-form"
                onSubmit={handleSubmit}
                className="p-8"
              >
                <div className="grid sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-[#0D1A4E] text-sm font-bold mb-1.5">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="お名前"
                      required
                      placeholder="山田 太郎"
                      className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#38BDF8] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[#0D1A4E] text-sm font-bold mb-1.5">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="電話番号"
                      required
                      placeholder="090-0000-0000"
                      className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#38BDF8] transition-colors"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-[#0D1A4E] text-sm font-bold mb-1.5">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="example@gmail.com"
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#38BDF8] transition-colors"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mb-6">
                  <div>
                    <label className="block text-[#0D1A4E] text-sm font-bold mb-1.5">
                      生年月日 / 年齢
                    </label>
                    <input
                      type="text"
                      name="生年月日または年齢"
                      placeholder="例：1985年4月生 / 39歳"
                      className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#38BDF8] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[#0D1A4E] text-sm font-bold mb-1.5">
                      警備の経験
                    </label>
                    <select
                      name="警備経験"
                      className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#38BDF8] transition-colors bg-white cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="未経験">未経験</option>
                      <option value="経験あり（1年未満）">経験あり（1年未満）</option>
                      <option value="経験あり（1年以上）">経験あり（1年以上）</option>
                    </select>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 border border-red-200 bg-red-50 rounded-md px-4 py-3 text-red-600 text-sm">
                    <i className="ri-error-warning-line mr-2"></i>{error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0D1A4E] hover:bg-[#162266] disabled:bg-gray-400 text-white py-4 rounded-md font-bold whitespace-nowrap transition-colors cursor-pointer text-sm"
                >
                  {submitting ? (
                    <span><i className="ri-loader-4-line animate-spin mr-2"></i>送信中...</span>
                  ) : (
                    <span><i className="ri-send-plane-line mr-2"></i>この内容で応募する</span>
                  )}
                </button>

                <p className="text-xs text-gray-400 mt-4 text-center">
                  ご入力いただいた情報は、採用選考にのみ使用いたします。
                </p>
              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}
